import Flex from '@Components/Bases/Flex';
import ChangeTypeModal from '@Components/Modal/ChangeTypeModal';
import { useDisclosure } from '@hooks/useDisclosure';
import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { GlobalError } from 'react-hook-form';
import { useNatureOfBusinessForm } from '../NatureOfBusinessFormProvider';
import {
  MarketingIndustryTypes,
  MarketingPolicyFormValues,
} from '../NatureOfBusinessFormProvider/types';

const initialPolicy = {
  descriptionTh: null,
  descriptionEn: null,
  industryCompetitionDescTh: null,
  industryCompetitionDescEn: null,
} as unknown as MarketingPolicyFormValues;

const MarketPoliciesSelectTypes = () => {
  const {
    setValue,
    getValues,
    watch,
    resetField,
    formState: { errors },
    trigger,
  } = useNatureOfBusinessForm();
  const [isDialogOpen, { open: openDialog, close: closeDialog }] =
    useDisclosure();

  const [marketType, completedSection] = watch([
    'marketingIndustry.type',
    'marketingIndustry.completedSection',
  ]);

  const { isCompletedTh = false, isCompletedEn = false } =
    completedSection ?? {};

  const [selectedMarketType, setSelectedMarketType] = useState<{
    prev: MarketingIndustryTypes | null;
    current: MarketingIndustryTypes | null;
  }>({ prev: marketType ?? null, current: marketType ?? null });

  useEffect(() => {
    if (selectedMarketType.prev === null) {
      if (marketType === MarketingIndustryTypes.OVERALL) {
        setValue('marketingIndustry.marketingPolicy', [
          initialPolicy as MarketingPolicyFormValues,
        ]);
        trigger('marketingIndustry.type');
      } else if (marketType === MarketingIndustryTypes.SPECIFIC) {
        const bizGroups = getValues('productInformation.businessGroupInfos');
        setValue(
          'marketingIndustry.marketingPolicy',
          bizGroups.map((group) => ({
            productNameTh: group.nameTh,
            productNameEn: group.nameEn,
            ...initialPolicy,
          }))
        );
        trigger('marketingIndustry.type');
      }
      setSelectedMarketType((prev) => ({ ...prev, prev: prev.current }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketType]);

  const handleConfirm = () => {
    if (selectedMarketType.current === MarketingIndustryTypes.OVERALL) {
      resetField('marketingIndustry.marketingPolicy', {
        keepTouched: false,
        defaultValue: [
          {
            ...initialPolicy,
            salesRatioIntroduction: {
              descriptionTh: null,
              descriptionEn: null,
            },
            salesRatioRemark: {
              descriptionTh: null,
              descriptionEn: null,
            },
            assets: [],
          },
        ],
      });
    }
    if (selectedMarketType.current === MarketingIndustryTypes.SPECIFIC) {
      const bizGroups = getValues('productInformation.businessGroupInfos');
      resetField('marketingIndustry.marketingPolicy', {
        keepTouched: false,
        defaultValue: bizGroups.map((group) => ({
          productNameTh: group.nameTh,
          productNameEn: group.nameEn,
          ...initialPolicy,
          salesRatioIntroduction: {
            descriptionTh: null,
            descriptionEn: null,
          },
          salesRatioRemark: {
            descriptionTh: null,
            descriptionEn: null,
          },
          assets: [],
        })),
      });
    }

    const event = new CustomEvent('marketingTypeChanged');
    window.dispatchEvent(event);

    setValue('marketingIndustry.type', selectedMarketType.current);
    trigger('marketingIndustry.type');
    setSelectedMarketType({
      prev: selectedMarketType.current,
      current: selectedMarketType.current,
    });
    closeDialog();
  };

  const handleClose = () => {
    setValue('marketingIndustry.type', selectedMarketType.prev);
    setSelectedMarketType((prev) => ({ ...prev, current: prev.prev }));
    closeDialog();
  };

  return (
    <>
      <Flex width={1} alignItems={'baseline'} mt={2}>
        <InputLabel
          sx={{ textAlign: 'right', width: 1, maxWidth: 350 }}
          required
        >
          กรุณาเลือกว่าท่านต้องการเปิดเผยข้อมูลในลักษณะใด
        </InputLabel>
        <Box ml={3}>
          <FormControl
            error={errors ? !!errors.marketingIndustry?.type : undefined}
          >
            <RadioGroup
              name="marketingIndustry.type"
              value={selectedMarketType.prev ?? ''}
              onChange={(event) => {
                const selectedValue = event.target
                  .value as MarketingIndustryTypes;

                if (selectedMarketType.prev === null) {
                  setValue('marketingIndustry.type', selectedValue);
                  setSelectedMarketType((prev) => ({
                    ...prev,
                    current: selectedValue,
                  }));
                } else if (selectedMarketType.prev === selectedValue) {
                } else {
                  openDialog();
                  setSelectedMarketType((prev) => ({
                    ...prev,
                    current: selectedValue,
                  }));
                }
              }}
            >
              <FormControlLabel
                value={MarketingIndustryTypes.OVERALL}
                control={
                  <Radio
                    color={
                      errors?.marketingIndustry?.type ? 'error' : 'primary'
                    }
                    disabled={isCompletedTh || isCompletedEn}
                  />
                }
                label="แจกแจงแบบภาพรวมของธุรกิจ"
              />
              <FormControlLabel
                value={MarketingIndustryTypes.SPECIFIC}
                control={
                  <Radio
                    color={
                      errors?.marketingIndustry?.type ? 'error' : 'primary'
                    }
                    disabled={isCompletedTh || isCompletedEn}
                  />
                }
                label="แจกแจงตามสายผลิตภัณฑ์ บริการ หรือกลุ่มธุรกิจ"
              />
              {errors?.marketingIndustry?.type && (
                <Typography color="error.main" variant="caption">
                  {(errors.marketingIndustry?.type as GlobalError).message}
                </Typography>
              )}
            </RadioGroup>
          </FormControl>
        </Box>
      </Flex>
      <ChangeTypeModal
        opened={isDialogOpen}
        close={handleClose}
        confirm={handleConfirm}
      />
    </>
  );
};

export default MarketPoliciesSelectTypes;
